import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import { useAuth } from '../context/AuthContext';
import { PATH_REGEX } from '../components/EntityPathField';
import { EntityPathOption } from '../types';
import Button from '../components/ui/Button';
import ActionRow from '../components/ui/ActionRow';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/pilot-ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/pilot-ui/alert-dialog';
import { Input } from '../components/pilot-ui/input';
import { Checkbox } from '../components/pilot-ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/pilot-ui/select';
import { Label } from '../components/pilot-ui/label';

interface User {
  id: number;
  email: string;
  created_at: string;
  has_password: boolean;
  permissions: string[];
  scopes: string[];
}

interface PermissionDef {
  name: string;
  description: string;
}

type ActiveModal =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; user: User }
  | { kind: 'delete'; user: User };

const UsersPage: React.FC = () => {
  const { id: selfId } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [catalog, setCatalog] = useState<PermissionDef[]>([]);
  const [pathOptions, setPathOptions] = useState<EntityPathOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ActiveModal>({ kind: 'none' });
  const opener = useRef<HTMLButtonElement | null>(null);
  const newUserButton = useRef<HTMLButtonElement>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersRes, catalogRes, optionsRes] = await Promise.all([
        axiosInstance.get<User[]>(`${config.accountsApiUrl}/accounts/`),
        axiosInstance.get<PermissionDef[]>(`${config.accountsApiUrl}/accounts/permissions/catalog`),
        axiosInstance.get<EntityPathOption[]>(`${config.accountsApiUrl}/accounts/entity-path-options`),
      ]);
      setUsers(
        [...usersRes.data].sort((a, b) => a.email.localeCompare(b.email))
      );
      setCatalog(catalogRes.data);
      setPathOptions(optionsRes.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openModal = (next: ActiveModal, trigger: HTMLButtonElement) => {
    opener.current = trigger;
    setModal(next);
  };
  const closeModal = () => {
    setModal({ kind: 'none' });
    window.requestAnimationFrame(() => {
      const target = opener.current?.isConnected ? opener.current : newUserButton.current;
      target?.focus();
    });
  };
  const closeAndRefresh = () => {
    if (modal.kind === 'delete') opener.current = newUserButton.current;
    closeModal();
    loadUsers();
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-card border border-border rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Users</h2>
          <Button
            ref={newUserButton}
            variant="primary"
            onClick={(event) => openModal({ kind: 'create' }, event.currentTarget)}
            title="Create a console account for someone. They get an email with a link to set their own password."
          >
            New User
          </Button>
        </div>

        <div className="bg-blue-50 border border-status-info-border text-blue-900 p-3 rounded-md text-sm mb-4">
          <strong>Note:</strong> New users receive an invite email with a password-setup link.
          To reset an existing user's password, open Edit and use "Send Password Reset Email".
        </div>

        {loading && <p className="text-muted-foreground">Loading…</p>}
        {error && (
          <div className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md mb-4">{error}</div>
        )}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-left border-collapse border border-border text-sm">
              <thead>
                <tr className="bg-gray-200">
                  <th className="px-3 py-2 border">ID</th>
                  <th className="px-3 py-2 border">Email</th>
                  <th className="px-3 py-2 border">Permissions</th>
                  <th className="px-3 py-2 border">Device Scopes</th>
                  <th className="px-3 py-2 border">Created</th>
                  <th className="px-3 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 border font-mono text-xs">{u.id}</td>
                    <td className="px-3 py-2 border font-mono text-xs">
                      {u.email}
                      {u.has_password === false && (
                        <span className="ml-2 inline-block border border-status-warning-border bg-status-warning text-status-warning-foreground text-xs px-2 py-0.5 rounded-sm font-sans">
                          Pending invite
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 border">
                      <div className="flex flex-wrap gap-1">
                        {u.permissions.length === 0 && (
                          <span className="text-xs text-muted-foreground">(none)</span>
                        )}
                        {u.permissions.map((p) => (
                          <span
                            key={p}
                            className="inline-block border border-status-info-border bg-status-info text-status-info-foreground text-xs px-2 py-0.5 rounded-sm font-mono"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 border">
                      <div className="flex flex-wrap gap-1">
                        {(u.scopes ?? []).length === 0 && (
                          <span className="text-xs text-muted-foreground">(none)</span>
                        )}
                        {(u.scopes ?? []).map((s) => (
                          <span
                            key={s}
                            className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-sm font-mono"
                          >
                            {s === '/' ? '/ (all)' : s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 border text-xs text-gray-600">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 border">
                      <div className="flex flex-wrap items-center gap-1">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(event) => openModal({ kind: 'edit', user: u }, event.currentTarget)}
                          title="Open this account to change its permissions and device scopes, or to email a password link."
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={(event) => openModal({ kind: 'delete', user: u }, event.currentTarget)}
                          disabled={u.id === selfId}
                          title={
                            u.id === selfId
                              ? "Can't delete your own account"
                              : 'Delete this account and the notes it owns. A confirmation opens first.'
                          }
                        >
                          Delete
                        </Button>
                        {u.id === selfId && <span className="self-center text-xs text-muted-foreground">Your account cannot be deleted here.</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 border text-center text-gray-600">
                    No users yet. Create an account to invite a colleague.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal.kind === 'create' && (
        <UserFormModal
          mode="create"
          catalog={catalog}
          pathOptions={pathOptions}
          onClose={closeModal}
          onSaved={closeAndRefresh}
        />
      )}
      {modal.kind === 'edit' && (
        <UserFormModal
          mode="edit"
          user={modal.user}
          catalog={catalog}
          pathOptions={pathOptions}
          onClose={closeModal}
          onSaved={closeAndRefresh}
        />
      )}
      {modal.kind === 'delete' && (
        <DeleteUserModal
          user={modal.user}
          onClose={closeModal}
          onDeleted={closeAndRefresh}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Scope-grant editor (user_entities rows)
// ---------------------------------------------------------------------------

const CUSTOM_SCOPE = '__custom__';

// A UTF-16 half with no partner. Postgres cannot store one, nor a NUL, so
// accounts-api refuses a grant holding either; the editor says so before Add.
const UNPAIRED_SURROGATE_RE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;
const isStorableText = (value: string) => !value.includes('\u0000') && !UNPAIRED_SURROGATE_RE.test(value);

/**
 * Edits the user's device-scope grants as a list: chips with remove buttons
 * plus an add-select fed by entity_path_options, a '/' superuser entry, and
 * a Custom… free-text mode for paths outside the lookup (e.g.
 * /residential/pilot/home-a/ to grant just one home's devices). Unlike device
 * entity_path, scopes need not extend a lookup option — '/' never appears
 * there — so custom entries are checked for shape and storable text only.
 * '%', '_' and '\' are ordinary characters in a grant.
 */
const ScopeListEditor: React.FC<{
  scopes: string[];
  options: EntityPathOption[];
  onChange: (scopes: string[]) => void;
}> = ({ scopes, options, onChange }) => {
  const [customMode, setCustomMode] = useState(false);
  const [customDraft, setCustomDraft] = useState('/');

  const addScope = (path: string) => {
    if (!scopes.includes(path)) onChange([...scopes, path].sort());
  };

  const customError = !PATH_REGEX.test(customDraft)
    ? "Path must start and end with '/', with no empty segments (e.g. /residential/pilot/home-a/)."
    : !isStorableText(customDraft)
      ? 'Path has a character that cannot be saved (a NUL or a broken Unicode character).'
      : scopes.includes(customDraft)
        ? 'Already granted.'
        : null;
  const customOffCatalog =
    !customError && customDraft !== '/' && !options.some((o) => customDraft.startsWith(o.path));

  const handleSelect = (v: string | null) => {
    if (v === CUSTOM_SCOPE) {
      setCustomMode(true);
      setCustomDraft('/');
    } else if (v) {
      addScope(v);
    }
  };

  const handleAddCustom = () => {
    if (customError) return;
    addScope(customDraft);
    setCustomMode(false);
    setCustomDraft('/');
  };

  return (
    <div className="border rounded-sm p-3 bg-gray-50 space-y-2">
      <div className="flex flex-wrap gap-1">
        {scopes.length === 0 && (
          <span className="text-xs text-muted-foreground">No scopes — user sees no devices.</span>
        )}
        {scopes.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-sm font-mono"
          >
            {s === '/' ? '/ (all devices)' : s}
            <Button
              variant="icon"
              size="sm"
              onClick={() => onChange(scopes.filter((x) => x !== s))}
              aria-label={`Remove scope ${s}`}
              title="Take this device scope off the account. Nothing changes until you press Save."
            >
              &times;
            </Button>
          </span>
        ))}
      </div>

      <Label htmlFor="add-device-scope" className="block text-sm font-medium">Add device scope</Label>
      <Select value={null} onValueChange={handleSelect}>
        <SelectTrigger id="add-device-scope" aria-describedby="scope-help" title="Pick a device scope to add to this account, or Custom… to type a path. Nothing changes until you press Save." className="w-full">
          <SelectValue placeholder="— add scope —" />
        </SelectTrigger>
        <SelectContent>
        {!scopes.includes('/') && <SelectItem value="/">/ — superuser (all devices)</SelectItem>}
        {options
          .filter((opt) => !scopes.includes(opt.path))
          .map((opt) => (
            <SelectItem key={opt.path} value={opt.path}>
              {opt.label ? `${opt.path} — ${opt.label}` : opt.path}
            </SelectItem>
          ))}
        <SelectItem value={CUSTOM_SCOPE}>Custom…</SelectItem>
        </SelectContent>
      </Select>
      <p id="scope-help" className="text-xs text-muted-foreground">Choose a known path or enter a custom scope. Changes apply when you Save.</p>

      {customMode && (
        <div>
          <Label htmlFor="custom-scope-path" className="block text-sm font-medium">Custom device scope</Label>
          <div className="flex gap-2">
            <Input
              id="custom-scope-path"
              type="text"
              aria-describedby={`custom-scope-help${customError ? ' custom-scope-error' : customOffCatalog ? ' custom-scope-warning' : ''}`}
              aria-invalid={!!customError || undefined}
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              autoFocus
              spellCheck={false}
              placeholder="/residential/pilot/home-a/"
              title="A device scope path: it starts and ends with '/'. The account sees every device whose entity path starts with it."
              className={`flex-1 px-3 py-2 border rounded-sm font-mono text-sm ${
                customError ? 'border-status-error-border' : ''
              }`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustom();
                }
              }}
            />
            <Button
              variant="primary"
              onClick={handleAddCustom}
              disabled={!!customError}
              aria-describedby={customError ? 'custom-scope-error' : undefined}
              title={customError ?? 'Add the path typed on the left as a device scope. Nothing changes until you press Save.'}
            >
              Add
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCustomMode(false)}
              title="Stop typing a custom path and go back to the list of known scopes."
            >
              Cancel
            </Button>
          </div>
          <p id="custom-scope-help" className="text-xs text-muted-foreground mt-1">
            Grants visibility to devices whose entity path starts with this prefix.
          </p>
          {customError ? (
            <p id="custom-scope-error" role="alert" className="text-xs text-red-700 mt-1">{customError}</p>
          ) : customOffCatalog ? (
            <p id="custom-scope-warning" className="text-xs text-amber-800 mt-1">
              Not under any known entity path option — devices must have this exact path
              prefix to be visible.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create/Edit modal
// ---------------------------------------------------------------------------

function sameSet(a: Iterable<string>, b: Iterable<string>): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((x) => right.has(x));
}

interface UserFormModalProps {
  mode: 'create' | 'edit';
  user?: User;
  catalog: PermissionDef[];
  pathOptions: EntityPathOption[];
  onClose: () => void;
  onSaved: () => void;
}

const UserFormModal: React.FC<UserFormModalProps> = ({ mode, user, catalog, pathOptions, onClose, onSaved }) => {
  const [email, setEmail] = useState(user?.email ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(user?.permissions ?? [])
  );
  const [scopes, setScopes] = useState<string[]>(user?.scopes ?? []);
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentMsg, setEmailSentMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // An edit that puts everything back as it was loaded (a scope removed and
  // added again, a box ticked and unticked) has nothing to save.
  const unchanged =
    mode === 'edit' &&
    !!user &&
    email.trim() === user.email &&
    sameSet(selected, user.permissions ?? []) &&
    sameSet(scopes, user.scopes ?? []);

  const togglePerm = (perm: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) {
        next.delete(perm);
      } else {
        next.add(perm);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setSubmitting(true);
    try {
      const permissions = Array.from(selected).sort();
      if (mode === 'create') {
        // Plan 28: no password — the backend creates the account and emails
        // an invite link with a 72h password-setup token.
        await axiosInstance.post(`${config.accountsApiUrl}/accounts/register`, {
          email: email.trim(),
          permissions,
          scopes,
        });
      } else if (user) {
        await axiosInstance.put(`${config.accountsApiUrl}/accounts/${user.id}`, {
          email: email.trim(),
          permissions,
          scopes,
        });
      }
      onSaved();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save'));
    } finally {
      setSubmitting(false);
    }
  };

  // Plan 28: admin-triggered emails from the edit modal. Reset for users with
  // a password, re-invite for ones still pending.
  const handleSendEmail = async () => {
    if (!user) return;
    setError(null);
    setEmailSentMsg(null);
    setSendingEmail(true);
    const action = user.has_password ? 'send-reset' : 'send-invite';
    try {
      await axiosInstance.post(`${config.accountsApiUrl}/accounts/${user.id}/${action}`, {});
      setEmailSentMsg(
        user.has_password
          ? `Reset email sent to ${user.email}.`
          : `Invite email re-sent to ${user.email}.`
      );
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const close = () => {
    if (!submitting) onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader className="relative shrink-0 border-b pb-3 pr-8">
          <DialogTitle>
            {mode === 'create' ? 'New User' : `Edit ${user?.email ?? ''}`}
          </DialogTitle>
          <DialogDescription>Set the account email, permissions and device scopes. Changes apply after Save.</DialogDescription>
          <Button
            variant="icon"
            onClick={close}
            disabled={submitting}
            className="absolute right-0 top-0 text-2xl"
            aria-label="Close"
            title="Close this form without saving."
          >
            &times;
          </Button>
        </DialogHeader>

        {/* A failed save remains visible above the scrolling form body. */}
        {error && (
          <div
            id="user-form-error"
            className="shrink-0 mx-6 mt-4 border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md text-sm font-medium flex items-start gap-2"
            role="alert"
          >
            <span aria-hidden>!</span>
            <div className="flex-1 whitespace-pre-wrap wrap-break-word">{error}</div>
            <Button
              variant="icon"
              onClick={() => setError(null)}
              aria-label="Dismiss"
              title="Hide this error message."
            >
              &times;
            </Button>
          </div>
        )}

        <div className="min-h-0 overflow-y-auto p-6 space-y-4">
          <div>
            <label htmlFor="user-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p id="user-email-help" className="text-xs text-muted-foreground mb-1">The address used to sign in and receive account emails.</p>
            <Input
              id="user-email"
              aria-describedby={error === 'Email is required' ? 'user-email-help user-form-error' : 'user-email-help'}
              aria-invalid={error === 'Email is required' || undefined}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              title="The address this person signs in with. Invite and password emails go here."
              className="w-full px-3 py-2 border rounded-sm"
              autoComplete="off"
            />
          </div>

          {mode === 'create' && (
            <div className="bg-blue-50 border border-status-info-border text-blue-900 p-3 rounded-md text-sm">
              An invite email with a password-setup link will be sent to this address.
              The link expires in 72 hours.
            </div>
          )}

          {mode === 'edit' && user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <Button
                variant="secondary"
                onClick={handleSendEmail}
                disabled={sendingEmail || submitting}
                title={user.has_password
                  ? 'Email this user a link, good for one hour, to choose a new password. Their current password keeps working until they do.'
                  : 'Email this user the invite again: a link, good for 72 hours, to set their first password.'}
              >
                {sendingEmail
                  ? 'Sending…'
                  : user.has_password
                    ? 'Send Password Reset Email'
                    : 'Resend Invite Email'}
              </Button>
              {emailSentMsg && (
                <p className="text-xs text-green-700 mt-1">{emailSentMsg}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {user.has_password
                  ? 'Emails the user a 1-hour link to choose a new password.'
                  : 'This user has not set a password yet — re-sends the 72-hour invite link.'}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="grid grid-cols-1 gap-2 border rounded-sm p-3 bg-gray-50">
              {catalog.map((p) => (
                <label key={p.name} className="flex items-start space-x-2 text-sm">
                  <Checkbox
                    checked={selected.has(p.name)}
                    onCheckedChange={() => togglePerm(p.name)}
                    className="h-4 w-4 mt-0.5"
                  />
                  <div>
                    <div className="font-mono text-xs text-blue-900">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.description}</div>
                  </div>
                </label>
              ))}
              {catalog.length === 0 && (
                <p className="text-xs text-muted-foreground">No permissions catalog loaded.</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Scopes
              <span className="text-xs text-muted-foreground ml-1 font-normal">
                (entity paths this user can see — takes effect at next login; a session
                already signed in keeps its old scopes until it expires, within 12 hours)
              </span>
            </label>
            <ScopeListEditor scopes={scopes} options={pathOptions} onChange={setScopes} />
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <ActionRow
            align="end"
            className="flex-1"
            reason={unchanged && 'No changes to save.'}
            reasonId="user-save-disabled-reason"
            reasonRole="status"
          >
            <Button
              variant="secondary"
              onClick={close}
              disabled={submitting}
              title="Close this form without saving."
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || sendingEmail || unchanged}
              aria-describedby={unchanged ? 'user-save-disabled-reason' : undefined}
              title={unchanged
                ? 'No changes to save'
                : mode === 'create'
                  ? 'Create this account and email the user a link to set their password.'
                  : 'Save the changes to this account.'}
            >
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </ActionRow>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

const DeleteUserModal: React.FC<{
  user: User;
  onClose: () => void;
  onDeleted: () => void;
}> = ({ user, onClose, onDeleted }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await axiosInstance.delete(`${config.accountsApiUrl}/accounts/${user.id}`);
      onDeleted();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            Delete <strong className="font-mono">{user.email}</strong>? This cannot be undone.
          </AlertDialogDescription>
          <p className="text-sm text-muted-foreground">
            Any notes owned by this account will be cascade-deleted.
          </p>
          {error && (
            <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md text-sm mb-3">{error}</div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            title="Close this dialog and keep the account."
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={submitting}
            title="Delete this account and the notes it owns. This cannot be undone."
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default UsersPage;
